<template>
  <!-- class="ma-0 pa-0" fill-height -->
  <div>
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card class="w-100" rounded="0" variant="flat" :loading="tabLoading">
          <v-card-title class="text-h6 px-4 pt-4 text-left">
            Services (Download/Import)
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-alert v-if="selectedMetadataGroup" type="info" class="mx-3">
                Services upload CSV must contain a column headers row and a
                "Postcode" column
              </v-alert>
            </v-row>
            <v-row>
              <v-col sm="12" md="6" class="my-auto">
                <v-select
                  v-if="metadataGroups.length"
                  :modelValue="selectedMetadataGroup"
                  :items="metadataGroups"
                  item-title="service"
                  item-value="id"
                  label="Select services type"
                  variant="outlined"
                  density="compact"
                  rounded="0"
                  class="mb-4"
                  @update:modelValue="selectedMetadataGroup = $event"
                  return-object
                ></v-select>
              </v-col>
              <v-col sm="12" md="6" align="start" class="my-auto">
                <v-btn
                  v-if="selectedMetadataGroup"
                  color="secondary"
                  variant="outlined"
                  rounded="0"
                  class="mb-8 mr-4"
                  @click="fetchServiceCSV"
                  :loading="downloading"
                >
                  Download Services
                </v-btn>
                <v-btn
                  v-if="selectedMetadataGroup"
                  color="primary"
                  rounded="0"
                  class="mb-8"
                  @click="showFileUploadDialog = true"
                  :loading="uploading"
                >
                  Upload Services
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="!selectedMetadataGroup" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card class="w-100" rounded="0">
          <v-card-text>
            <div
              class="d-flex justify-center align-center mx-auto"
              style="width: 400px"
            >
              <AvatarState avatarSize="102" icon="mdi-map-marker" iconSize="54">
                <template #title>Select a Service Type</template>
                <template #body>
                  Select a service type from the dropdown to get started
                  downloading or uploading services
                </template>
              </AvatarState>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- dialogs -->
    <DynamicDialog
      :show="showFileUploadDialog"
      @update:showDyamicDialog="(state) => (showFileUploadDialog = state)"
      @dialogCancel="showFileUploadDialog = false"
      @dialogOk="
        showFileUploadDialog = false;
        showUploadConfirmationDialog = true;
      "
      :okBtnDisabled="!selectedFile || !fileInputValid"
      max-width="800"
    >
      <template v-slot:title
        >Upload Services for {{ selectedMetadataGroup.service }}</template
      >
      <template v-slot:content>
        <v-alert type="warning" class="ma-4">
          Uploading a CSV file will overwrite existing services for the selected
          metadata group. Ensure that the CSV file contains all services and is
          formatted correctly to avoid data loss.
        </v-alert>
        <v-form v-model="fileInputValid">
          <v-file-input
            v-model="selectedFile"
            label="Select a CSV file to upload"
            accept=".csv"
            variant="outlined"
            density="compact"
            rounded="0"
            :rules="[
              (value) => {
                return !!value || 'File is required';
              },
              (value) => {
                return (
                  (value && value.type === 'text/csv') ||
                  'Only CSV files are allowed'
                );
              },
            ]"
            show-size
            @change="
              (event) => {
                if (event.target.files.length > 0) {
                  selectedFile = event.target.files[0];
                }
              }
            "
          ></v-file-input>
        </v-form>
      </template>
      <template v-slot:okBtnTitle>Upload Services</template>
    </DynamicDialog>

    <DynamicDialog
      :show="showUploadConfirmationDialog"
      @update:showUploadConfirmationDialog="
        (state) => (showUploadConfirmationDialog = state)
      "
      @dialogCancel="showUploadConfirmationDialog = false"
      @dialogOk="
        showUploadConfirmationDialog = false;
        uploadServices();
      "
      max-width="800"
    >
      <template v-slot:title>Destructive Operation</template>
      <template v-slot:content>
        Are you sure you want to upload services for
        {{ selectedMetadataGroup.service }} and overwrite existing?
      </template>
      <template v-slot:okBtnTitle>Continue</template>
    </DynamicDialog>
  </div>
</template>

<script>
// import FormsInputsAndControls from '@/components/formsInputsAndControls'
import { systemMessages } from "@/mixins/SystemMessages";
import DynamicDialog from "@/components/DynamicDialog.vue";
import AvatarState from "@/components/AvatarState.vue";

export default {
  name: "SDServices",
  data: () => ({
    metadataGroups: [],
    selectedMetadataGroup: null,
    showFileUploadDialog: false,
    showUploadConfirmationDialog: false,
    selectedFile: null,
    downloading: false,
    tabLoading: false,
    uploading: false,
    fileInputValid: true,
  }),
  components: {
    DynamicDialog,
    AvatarState,
  },
  mixins: [systemMessages],
  mounted() {
    this.fetchmetadataGroups();
  },
  methods: {
    async fetchmetadataGroups() {
      this.tabLoading = true;
      this.metadataGroups = this.$axios
        .get("/sd-services-metadata")
        .then((response) => {
          this.tabLoading = false;
          this.metadataGroups = response.data;
        })
        .catch((error) => {
          this.tabLoading = false;
          this.errorMsg({
            title: "Error fetching metadata groups",
            message: error.message,
          });
        });
    },
    fetchServiceCSV() {
      this.downloading = true;

      this.$axios
        .get(`/sd-services/download/${this.selectedMetadataGroup.id}`)
        .then((response) => {
          this.downloading = false;

          this.downloadCSVStream(
            response,
            `services_${this.selectedMetadataGroup.service}`,
          );
        })
        .catch((error) => {
          this.downloading = false;
          // if error is 4xx
          if (
            error.response &&
            error.response.status >= 400 &&
            error.response.status < 500
          ) {
            this.warnMsg({
              title: "No Services to download",
              message:
                error.response.data.message ||
                "No services available for download.",
            });
            return;
          } else {
            this.errorMsg({
              title: "Error downloading services",
              message: error.message,
            });
          }
        });
    },
    uploadServices() {
      this.uploading = true;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      this.$axios
        .post(
          `/sd-services/bulk-import/${this.selectedMetadataGroup.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((response) => {
          this.uploading = false;
          this.selectedFile = null; // Reset the file input

          const message =
            response.data.message +
              ` <br />(${response.data.skipped_rows} records skipped e.g blank rows)` ||
            "Services uploaded successfully.";
          this.successMsg({
            title: "Services Uploaded Successfully",
            message: message,
            timeout: 5000,
          });
        })
        .catch((error) => {
          this.uploading = false;
          this.selectedFile = null; // Reset the file input

          // csv validation errors 422
          if (error.response && error.response.status === 422) {
            //determine if Laravel validator error or csv parse error
            let errorResponseType = "csv";
            let errorTitle =
              error.response.headers["services-validation-title"];
            let errorMessage =
              error.response.headers["services-validation-message"];
            if (error.response.data?.message) {
              errorResponseType = "validator";
              errorTitle = "Unable to Upload Services";
              errorMessage = error.response.data.message;
            }
            this.warnMsg({
              title: errorTitle,
              message: errorMessage,
              timeout: -1,
            });
            //download streamed csv validation file if csv errors
            if (errorResponseType === "csv") {
              this.downloadCSVStream(
                error.response,
                `errors_${this.selectedMetadataGroup.service}`,
              );
            }
          } else {
            //general errors
            this.errorMsg({
              title: "Error uploading services",
              message: error.response?.message || "Failed to upload services.",
            });
          }
        });
    },
    downloadCSVStream(response, filename) {
      // Create a blob from the response data and trigger download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename.replace(/ /g, "_")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
  },
};
</script>

<style scoped></style>
